import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import { Link, useNavigate, useSearch, useRouter } from '@tanstack/react-router';
import { toast } from '../../utils/toast';
import { extractFieldErrors, hasErrorCode } from '../../utils/apiError';

const HERO_IMAGES = ['/hero_back.png', '/hero_back_2.jpg', '/hero_back_3.jpg'];
const INTERVAL_MS = 6000;

import { useAuth } from '../../hooks/useAuth';
import { mapUserFromAuthResponse } from '../../contexts/AuthContext';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useGoogleLoginMutation, useManualSignInMutation } from '../../api/auth';
import { useAppleAuth } from '../../hooks/useAppleAuth';
import { signupStore } from '../../utils/signupStore';
import { extractAuthSession } from '../../utils/authSession';
import { AuthHeader } from './AuthHeader';

type ApiUser = {
  id?: string | number;
  email?: string;
  username?: string;
  full_name?: string;
  fullName?: string;
  city?: string;
  state?: string;
  country?: string;
  genres?: string[];
  tags?: string[];
  user_types?: unknown[];
  prof_pics?: string;
  profPics?: string;
  profile_incomplete?: boolean;
  needs_profile_update?: boolean;
  role?: 'creator' | 'fan' | 'venue' | null;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Removed local mapUserFromAuthResponse as it is now shared from AuthContext

export const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  // Read intended destination set by protected routes
  const { redirect: redirectTo } = useSearch({ from: '/sign-in' });
  const router = useRouter();

  const goAfterLogin = () => {
    if (redirectTo && redirectTo !== '/sign-in') {
      router.history.replace(String(redirectTo));
    } else {
      navigate({ to: '/', replace: true });
    }
  };
  const [currentIndex, setCurrentIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [useGoogleHint, setUseGoogleHint] = useState(false);

  const manualSignInMutation = useManualSignInMutation();
  const googleMutation = useGoogleLoginMutation();
  const { handleAppleSignIn } = useAppleAuth();

  const advance = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(advance, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [advance]);

  const validate = () => {
    const nextErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!emailRegex.test(email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!password) {
      nextErrors.password = 'Password is required.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEmailSignIn = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await manualSignInMutation.mutateAsync({
        email: email.trim().toLowerCase(),
        password,
      });

      const token = data?.tokens?.access ?? data?.access ?? data?.token ?? data?.access_token;
      const refreshToken = data?.tokens?.refresh ?? data?.refresh ?? data?.refresh_token;

      if (!token) {
        toast.error('Login succeeded but no access token was returned.');
        return;
      }

      const user = mapUserFromAuthResponse((data?.data ?? data?.user ?? data) as ApiUser);
      login(token, user, refreshToken);

      if (user.profileIncomplete) {
        toast.info('Complete your profile details to unlock all features.');
      }

      goAfterLogin();
    } catch (err) {
      // toast.error handled by mutation; surface field-level errors inline
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.email || fieldErrors.password) {
        setErrors({ email: fieldErrors.email, password: fieldErrors.password });
      }
      setUseGoogleHint(hasErrorCode(err, 'GOOGLE_ACCOUNT_USE_GOOGLE_SIGN_IN'));
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;

    setIsLoading(true);
    try {
      // credentialResponse.credential IS the ID Token (JWT)
      const data = await googleMutation.mutateAsync(credentialResponse.credential);

      const { rawUser, accessToken, refreshToken } = extractAuthSession(data);

      if (accessToken) {
        // Authenticated user
        const mappedUser = mapUserFromAuthResponse((rawUser ?? {}) as ApiUser);
        login(accessToken, mappedUser, refreshToken ?? undefined);

        if (mappedUser.profileIncomplete) {
          toast.info('Complete your profile details to unlock all features.');
        }

        goAfterLogin();
      } else if (rawUser?.email) {
        // New user - move to next step of signup flow
        signupStore.set({
          flowActive: true,
          email: rawUser.email,
          role: 'creator',
          fullName: rawUser.first_name || rawUser.full_name || '',
          profilePicUrl: rawUser.prof_pics || rawUser.google_prof_pics || '',
          googleSignedUp: true,
        });

        toast.info('Google account verified. Please complete your registration.');
        navigate({ to: '/personal-information' });
      } else {
        console.error('Incomplete data in backend response:', data);
        toast.warn('Login succeeded but no session token or email was received.');
      }
    } catch (error) {
      console.error('Google login backend sync failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleLogin = () => {
    handleAppleSignIn();
  };

  return (
    <div className="relative min-h-screen w-full flex bg-black overflow-hidden font-['Inter']">
      {/* Background Carousel */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="popLayout">
          <motion.img
            key={currentIndex}
            src={HERO_IMAGES[currentIndex]}
            alt="Background"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
        </AnimatePresence>
        {/* Dark overlays to make text legible */}
        <div className="absolute inset-0 bg-linear-to-t from-black via-black/40 to-black/20" />
        <div className="absolute inset-0 w-full lg:w-2/3 bg-linear-to-r from-black via-black/80 to-transparent" />
      </div>

      <AuthHeader />

      {/* Main Content Area */}
      <div className="relative z-10 flex flex-col justify-center w-full max-w-xl px-5 sm:px-8 md:px-12 lg:px-20">
        <div className="w-full max-w-[24rem] sm:max-w-md">
          <h1
            className="text-white text-[26px] sm:text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Take back the Culture
          </h1>
          <p className="text-[#BEBEBE] text-[15px] mb-8">
            Get booked, get discovered and power your local scene
          </p>

          <form className="flex flex-col gap-4" onSubmit={handleEmailSignIn}>
            <div className="relative flex items-center bg-[#141414]/80 backdrop-blur-sm border border-white/10 rounded-xl h-12 px-4">
              <FiMail className="text-zinc-500 mr-3" size={18} />
              <input
                type="email"
                placeholder="Enter Email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                required
                className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-500 w-full focus:outline-none focus:ring-0"
              />
            </div>
            {errors.email ? <p className="text-red-400 text-xs -mt-2">{errors.email}</p> : null}

            <div className="relative flex items-center bg-[#141414]/80 backdrop-blur-sm border border-white/10 rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors">
              <FiLock className="text-zinc-500 mr-3" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                required
                className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-500 w-full focus:outline-none focus:ring-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer shrink-0 ml-2"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {errors.password ? (
              <p className="text-red-400 text-xs -mt-2">{errors.password}</p>
            ) : null}

            {useGoogleHint ? (
              <p className="text-amber-400 text-xs -mt-2">
                This account uses Google sign-in. Please continue with Google below.
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isLoading}
              className="h-12 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium text-[15px] rounded-xl transition-colors shadow-lg shadow-blue-500/20 disabled:opacity-50"
            >
              {isLoading ? 'Processing...' : 'Login'}
            </button>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-[#3B82F6] hover:text-blue-400 text-[14px] font-medium transition-colors"
              >
                Forgot Password?
              </Link>
            </div>

            <div className="flex justify-center">
              <p className="text-zinc-400 text-sm">
                Don&apos;t have an account?{' '}
                <Link to="/sign-up" className="text-[#3B82F6] hover:text-blue-400 font-medium">
                  Sign up
                </Link>
              </p>
            </div>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="text-zinc-500 text-[13px]">or</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          {/* Social Logins */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleAppleLogin}
              disabled={isLoading}
              className="flex items-center justify-center gap-3 h-12 bg-transparent hover:bg-white/5 border border-white/10 rounded-xl text-white font-medium text-[14px] transition-colors disabled:opacity-50"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.19 2.24-.86 3.43-.88 1.5-.02 2.73.59 3.44 1.59-3.41 1.95-2.88 6.42.34 7.68-.78 1.52-1.74 3.12-2.29 3.78zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple
            </button>
            <div className="relative group">
              <button
                disabled={isLoading}
                className="flex items-center justify-center gap-3 w-full h-12 bg-transparent hover:bg-white/5 border border-white/10 rounded-xl text-white font-medium text-[14px] transition-colors disabled:opacity-50"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.16v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.16C1.43 8.55 1 10.22 1 12s.43 3.45 1.16 4.93l2.48-1.92.12-.92z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.16 7.07l3.68 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>
              <div className="absolute inset-0 opacity-0 cursor-pointer overflow-hidden flex justify-center scale-[1.5]">
                <GoogleLogin
                  onSuccess={onGoogleSuccess}
                  onError={() => console.error('Google Login Error')}
                  useOneTap
                  theme="filled_black"
                  shape="rectangular"
                  width="400"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
